import type { LineNumber, LineSyncResult } from '../api/monitor';
import { formatCheckedAt } from './health';
import { pageRange } from './pagination';

/**
 * Formata o número como o design mostra: `(62) 98455-2210`.
 *
 * O banco guarda só dígitos com DDD e sem DDI, mas a coluna é livre — o que não
 * couber nos formatos conhecidos volta como veio, em vez de ser recortado.
 */
export function formatLine(line: string): string {
  const digits = line.replace(/\D/g, '');

  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return line;
}

/**
 * `1,4 / 20 GB` — nulo quando a última importação de consumo não trouxe a linha.
 *
 * Consumo e franquia faltam separadamente, e a linha mostra o que se sabe. Sem
 * franquia conhecida (plano compartilhado, linha sem pacote de dados) o consumo
 * aparece sozinho: escondê-lo apagaria o número que a linha *tem* por falta de
 * um que ela não tem. Só quando não se sabe nenhum dos dois é que não há o que
 * escrever.
 */
export function formatUsage(consumption: LineNumber['consumption']): string | null {
  if (!consumption) return null;

  const gb = (value: number) => value.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  const { consumed, hired } = consumption;

  if (consumed !== null && hired !== null) {
    // O percentual é sempre sobre o contratado no SGC; sobre a cota do portal
    // ele não vem, e a dica explica (`usageHint`).
    const share = consumption.percentage ? ` · ${consumption.percentage.replace('.', ',')}` : '';

    return `${gb(consumed)} / ${gb(hired)} GB${share}`;
  }
  if (consumed !== null) {
    // "Não encontrada" é resposta do SGC; "não informada" é não ter resposta.
    // O texto fala da franquia, que é o que falta — a linha existe e consumiu.
    return `${gb(consumed)} GB · ${
      consumption.notFoundInSgc ? 'franquia não encontrada no SGC' : 'franquia não informada'
    }`;
  }
  if (hired !== null) return `sem consumo · ${gb(hired)} GB contratados`;

  return null;
}

/**
 * O porquê do texto de consumo, para a dica ao passar o mouse — só quando o
 * texto sozinho não explica. Hoje é o caso da franquia não encontrada no SGC:
 * quem lê pode achar que a linha é que não existe.
 */
export function usageHint(consumption: LineNumber['consumption']): string | null {
  if (consumption?.hiredSource === 'portal') {
    return 'Total = cota do portal da operadora: a linha não tem contrato no SGC, por isso não há % consumido.';
  }

  if (consumption?.hired === null && consumption.notFoundInSgc) {
    return 'O SGC não tem esta linha ativa com nenhum cliente, por isso não há franquia contratada para mostrar.';
  }

  return null;
}

/**
 * Segunda linha da lista: quem é o titular e de quem é a conta.
 *
 * O master é o que interessa, mas nem toda conta tem vínculo ativo; nesses
 * casos o login da conta no portal é o único dono conhecido da linha.
 */
export function lineOwner(number: LineNumber): string {
  return [number.name, number.master ?? `Conta ${number.account}`].filter(Boolean).join(' · ');
}

/**
 * O que o toast diz depois de sincronizar.
 *
 * O texto muda com a origem porque as duas operadoras respondem coisas
 * diferentes: da Vivo vem a leitura do momento; da Claro vem o que a importação
 * das 08:00 gravou, e omitir isso faria o usuário ler um número de horas atrás
 * como se fosse de agora. Sem consumo nenhum o aviso é de erro — o clique não
 * produziu o que ele pedia, mesmo sem nada ter falhado.
 */
export function syncMessage(result: LineSyncResult): { text: string; kind: 'ok' | 'error' } {
  const { number, source } = result;
  const line = formatLine(number.line);
  const usage = formatUsage(number.consumption);

  if (source === 'operator') {
    return {
      text: usage
        ? `Linha ${line} sincronizada com a ${number.operator.name}: ${usage}.`
        : `A ${number.operator.name} respondeu sem consumo para a linha ${line}.`,
      kind: usage ? 'ok' : 'error',
    };
  }

  if (!usage) {
    return {
      text: `A ${number.operator.name} apura o consumo uma vez por dia e a última importação não trouxe a linha ${line}.`,
      kind: 'error',
    };
  }

  return {
    text: `Linha ${line}: ${usage}, da importação diária da ${number.operator.name} (${formatCheckedAt(
      number.consumption?.updatedAt ?? null,
    )}).`,
    kind: 'ok',
  };
}

/**
 * O aviso de uma sincronização pedida pelo número.
 *
 * Costuma ser uma linha só; são duas quando o mesmo número existe nas duas
 * operadoras (portabilidade), e aí as duas viram uma frase — dizer só "linha
 * sincronizada" esconderia que uma delas é de outra operadora.
 */
export function syncSummary(results: LineSyncResult[]): { text: string; kind: 'ok' | 'error' } {
  const messages = results.map(syncMessage);

  return {
    text: messages.map((message) => message.text).join(' '),
    kind: messages.some((message) => message.kind === 'error') ? 'error' : 'ok',
  };
}

/** `1–50 de 1.482 linhas`, o rodapé da paginação. */
export function pageLabel(page: number, pageSize: number, total: number): string {
  if (total === 0) return 'Nenhuma linha encontrada';

  return `${pageRange(page, pageSize, total)} linhas`;
}
